import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Text, TextInput, SelectInput, Button, DateInput, Icon } from '@/ui-kits';
import { DataTable } from '@/components';
import { DataTableColumn, SelectInputitem, SpecialistPayout, GetSpecialistPayoutsRequestPayload } from '@/interface';
import { useSelector, useDispatch } from 'react-redux';
import {
  specialistPayoutsSelector,
  specialistPayoutsCurrentPageSelector,
  specialistPayoutsTotalPagesSelector,
  specialistPayoutsTotalResultsSelector,
  financeAdminStatus,
  financeAdminFailureSelector,
  financeAdminSuccessSelector,
  financeAdminChangePayoutStatusLoadingSelector,
} from '@/services/redux/selectors';
import { getSpecialistPayoutsRequest, changePayoutStatusRequest, resetFinanceAdminMessage } from '@/services/redux/slices';
import { ErrorCodes, getErrorMessage, SuccessCodes } from '@/utils/getMessages';
import { useToast, useDebounceState } from '@/hooks';
import { formatDate } from '@/utils/common_functions';
import { NA, SpecialistPayoutStatus } from '@/constants';
import { LoaderCircle } from 'lucide-react';

const statusOptions: SelectInputitem[] = [
  { id: SpecialistPayoutStatus.Pending, label: 'Pending' },
  { id: SpecialistPayoutStatus.Paid, label: 'Paid' },
  { id: SpecialistPayoutStatus.Cancelled, label: 'Cancelled' },
];

export const SpecialistPayouts: React.FC = () => {
  const { showToast } = useToast();
  const dispatch = useDispatch();

  // Selectors
  const payouts = useSelector(specialistPayoutsSelector);
  const currentPageFromRedux = useSelector(specialistPayoutsCurrentPageSelector);
  const totalPages = useSelector(specialistPayoutsTotalPagesSelector);
  const totalResults = useSelector(specialistPayoutsTotalResultsSelector);
  const isLoading = useSelector(financeAdminStatus);
  const changePayoutStatusLoading = useSelector(financeAdminChangePayoutStatusLoadingSelector);
  const failure = useSelector(financeAdminFailureSelector) as ErrorCodes;
  const success = useSelector(financeAdminSuccessSelector) as SuccessCodes;

  // States
  const [search, setSearch, immediateSearch] = useDebounceState<string>('');
  const [selectedStatus, setSelectedStatus, immediateSelectedStatus] = useDebounceState<SelectInputitem | null>(null);
  const [fromDate, setFromDate, immediateFromDate] = useDebounceState<Date | null>(null);
  const [toDate, setToDate, immediateToDate] = useDebounceState<Date | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingRowId, setEditingRowId] = useState<number | null>(null); // Use case_id which is unique
  const [editedStatus, setEditedStatus] = useState<number | null>(null); // Store enum value
  const prevFiltersRef = useRef({ search, selectedStatus, fromDate, toDate });
  const [emptyScreen, setEmptyScreen] = useState(false);

  // Fetch payouts
  const fetchPayouts = useCallback(() => {
    const payload: GetSpecialistPayoutsRequestPayload = {
      page: currentPage,
    };
    if (search) {
      payload.search_text = search;
    }
    if (fromDate) {
      payload.start_date = fromDate.toISOString().split('T')[0];
    }
    if (toDate) {
      payload.end_date = toDate.toISOString().split('T')[0];
    }
    if (selectedStatus) {
      payload.status = selectedStatus.id;
    }
    dispatch(getSpecialistPayoutsRequest(payload));
  }, [currentPage, search, fromDate, toDate, selectedStatus, dispatch]);

  // Reset page when filters change
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged =
      prevFilters.search !== search ||
      prevFilters.selectedStatus?.id !== selectedStatus?.id ||
      prevFilters.fromDate?.getTime() !== fromDate?.getTime() ||
      prevFilters.toDate?.getTime() !== toDate?.getTime();

    if (filtersChanged) {
      setCurrentPage(1);
      prevFiltersRef.current = { search, selectedStatus, fromDate, toDate };
    }
  }, [search, selectedStatus, fromDate, toDate]);

  // Side effects
  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  useEffect(() => {
    if (failure) {
      if (['E-10044', 'E-10054'].includes(failure)) {
        // if failure is E-10054, set empty screen to true
        if (failure === 'E-10054') {
          setEmptyScreen(true);
        }
        return;
      }
      showToast(getErrorMessage(failure), 'failure');
    }
    return () => {
      dispatch(resetFinanceAdminMessage());
    };
  }, [failure, showToast, dispatch]);

  useEffect(() => {
    if (success && String(success) === 'S-10105') {
      showToast('Payout status updated successfully', 'success');
      setEditingRowId(null);
      setEditedStatus(null);
      // Refetch payouts after successful status change
      fetchPayouts();
    }
    return () => {
      dispatch(resetFinanceAdminMessage());
    };
  }, [success, showToast, dispatch, fetchPayouts]);

  // Handlers
  const handleEdit = (row: SpecialistPayout) => {
    setEditingRowId(row.case_id);
    setEditedStatus(row.specialist?.status?.id || null);
  };

  const handleSave = (caseId: number) => {
    if (editedStatus !== null && editingRowId) {
      dispatch(changePayoutStatusRequest({ id: caseId, status: editedStatus }));
    }
  };

  const handleCancel = () => {
    setEditingRowId(null);
    setEditedStatus(null);
  };

  const resetFilter = () => {
    setSearch('');
    setSelectedStatus(null);
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
  };

  const isFilterActive = useMemo(() => {
    return !!search || !!selectedStatus || !!fromDate || !!toDate;
  }, [search, selectedStatus, fromDate, toDate]);

  // Update currentPage when Redux state changes
  useEffect(() => {
    if (currentPageFromRedux > 0) {
      setCurrentPage(currentPageFromRedux);
    }
  }, [currentPageFromRedux]);

  const columns: DataTableColumn<SpecialistPayout>[] = useMemo((): DataTableColumn<SpecialistPayout>[] => {
    return [
      {
        name: 'case_id',
        title: <Text>CASE ID</Text>,
        render: row => `${row.case_id}`,
        headerAlign: 'left',
        width: { minWidth: '120px', maxWidth: '150px' },
        align: 'left',
      },
      {
        name: 'specialist_id',
        title: <Text>SPECIALIST ID</Text>,
        render: row => `${row.specialist?.id || NA}`,
        headerAlign: 'center',
        width: { minWidth: '150px', maxWidth: '200px' },
        align: 'center',
      },
      {
        name: 'specialist_name',
        title: <Text>SPECIALIST NAME</Text>,
        render: row => (
          <div className="whitespace-normal break-words">
            {row.specialist?.name ? `Dr. ${row.specialist.name}` : NA}
          </div>
        ),
        headerAlign: 'left',
        width: { minWidth: '200px', maxWidth: '250px' },
        align: 'left',
      },
      {
        name: 'hospital_name',
        title: <Text>HOSPITAL NAME</Text>,
        render: row => (
          <div className="whitespace-normal break-words">{row.specialist?.hospital || NA}</div>
        ),
        headerAlign: 'left',
        width: { minWidth: '250px', maxWidth: '300px' },
        align: 'left',
      },
      {
        name: 'amount',
        title: <Text>AMOUNT</Text>,
        render: row => `₹${row.specialist?.amount?.toLocaleString() || '0'}`,
        headerAlign: 'right',
        width: { minWidth: '100px', maxWidth: '150px' },
        align: 'center',
      },
      {
        name: 'status',
        title: <Text>STATUS</Text>,
        render: row => {
          const isEditing = editingRowId === row.case_id;
          if (isEditing) {
            // Filter out Cancelled option when editing
            const editStatusOptions = statusOptions.filter(
              option => option.id !== SpecialistPayoutStatus.Cancelled
            );
            return (
              <SelectInput
                value={editedStatus || row.specialist?.status?.id || SpecialistPayoutStatus.Pending}
                onChange={item => {
                  setEditedStatus(item.id as number);
                }}
                options={editStatusOptions}
                wrapperClassName="border !py-0.5 ml-4"
                className="min-w-[120px]"
                textClassName="!text-sm !py-1"
                itemTextClassName="!font-FigtreeRegular !text-sm"
                disabled={changePayoutStatusLoading}
              />
            );
          }
          return row.specialist?.status?.label || NA;
        },
        headerAlign: 'center',
        width: { minWidth: '150px', maxWidth: '200px' },
        align: 'center',
      },
      {
        name: 'payout_date',
        title: <Text>PAYOUT DATE & TIME</Text>,
        render: row => formatDate(row.payout_date, 'dd-MMM-yyyy, HH:mm:ss') || NA,
        headerAlign: 'center',
        width: { minWidth: '250px', maxWidth: '300px' },
        align: 'center',
      },
      {
        name: 'actions',
        title: <Text>ACTIONS</Text>,
        render: row => {
          const isPending = row.specialist?.status?.id === SpecialistPayoutStatus.Pending;
          const isEditing = editingRowId === row.case_id;

          if (isEditing) {
            return (
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => handleSave(row.case_id)}
                  className="flex justify-center"
                  disabled={changePayoutStatusLoading}
                >
                  <Icon type="green_tick_btn" className="size-5" />
                </button>
                <button onClick={handleCancel} className="flex justify-center" disabled={changePayoutStatusLoading}>
                  <Icon type="cross_btn_red" className="size-5" />
                </button>
              </div>
            );
          }

          if (isPending) {
            return (
              <div className="flex justify-center">
                <button onClick={() => handleEdit(row)} className="flex justify-center">
                  <Icon type="edit_input" className="size-4" />
                </button>
              </div>
            );
          }

          return (
            <div className="flex justify-center">
              <Text>--</Text>
            </div>
          );
        },
        headerAlign: 'center',
        width: { minWidth: '100px', maxWidth: '120px' },
        align: 'center',
      },
    ];
  }, [editingRowId, editedStatus, changePayoutStatusLoading]);

  // Show loading state
  if (isLoading && !isFilterActive) {
    return (
      <div className="flex items-center justify-center px-4 sm:px-6 py-4 sm:py-6">
        <span className="text-ui_grey">Loading...</span>
        <LoaderCircle className="size-4 animate-spin text-ui_grey ml-2" />
      </div>
    );
  }

  // Empty state
  if (!isLoading && emptyScreen) {
    return (
      <div className="flex flex-col grow p-2 sm:p-4 md:p-6 lg:p-8">
        <Text variant="subheading" className="!font-FigtreeMedium text-deep_blue">
        Track and manage specialist and hospital payments in one place
        </Text>

        <div className="flex flex-col items-center justify-center flex-1 py-12">
          <Text variant="heading" className="text-deep_blue mb-2 !font-FigtreeBold">
            No payouts yet
          </Text>
          <Text variant="body" className="text-deep_blue text-center !text-lg !font-FigtreeRegular">
           Specialist and hospital payouts will appear here once consultations are completed and payouts are released by the admin.
          </Text>
        </div>
      </div>
    );
  }

  // Table view with data
  return (
    <div className="flex flex-col grow p-2 sm:p-4 md:p-6 lg:p-8">
      <Text
        variant="subheading"
        className="!font-FigtreeMedium text-deep_blue mb-2 overflow-hidden text-ellipsis !text-xl sm:!text-2xl break"
      >
        Track and manage specialist and hospital payments in one place
      </Text>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 my-4 mb-6 flex-wrap">
        <TextInput
          value={immediateSearch}
          onChangeText={setSearch}
          wrapperClassName="border pl-2"
          className="w-full sm:flex-1 sm:min-w-[350px] sm:max-w-[450px]"
          leftIcon="search_blue"
          name="search"
          placeholder="Search by Case ID, Specialist ID or Name"
        />
        <div className="flex flex-row gap-4 w-full sm:flex-none sm:contents">
          <DateInput
            value={immediateFromDate}
            onChangeDate={setFromDate}
            placeholder="From"
            wrapperClassName="border rounded-lg"
            className="flex-[1.1] w-full sm:flex-1 sm:min-w-[100px] sm:max-w-[150px]"
            textClassName="text-start text-ui_blue whitespace-nowrap overflow-hidden text-ellipsis"
            maxValue={toDate ?? new Date()}
          />
          <DateInput
            value={immediateToDate}
            onChangeDate={setToDate}
            placeholder="To"
            wrapperClassName="border rounded-lg"
            className="flex-1 w-full sm:flex-1 sm:min-w-[100px] sm:max-w-[150px]"
            textClassName="text-start text-ui_blue whitespace-nowrap overflow-hidden text-ellipsis"
            minValue={fromDate ?? undefined}
            maxValue={new Date()}
          />
        </div>
        <div className="flex flex-row gap-4 w-full sm:flex-none sm:contents">
          <SelectInput
            wrapperClassName="border"
            name="status"
            className="w-1/2 !sm:flex-1 sm:min-w-[100px] sm:max-w-[150px]"
            placeholder="Status"
            options={statusOptions}
            value={immediateSelectedStatus?.id || ''}
            onChange={setSelectedStatus}
          />
          {isFilterActive && (
            <Button
              title="Reset Filters"
              variant="secondary"
              onClick={resetFilter}
              className="w-[150px] sm:w-fit sm:flex-none self-start sm:self-auto flex-shrink-0 text-xs sm:text-base px-2 sm:px-4"
            />
          )}
        </div>
      </div>

      <DataTable
        data={payouts}
        columns={columns}
        totalPages={totalPages}
        currentPage={currentPage}
        totalResult={totalResults}
        onPageChange={setCurrentPage}
        pageSize={10}
        loading={isLoading}
        errorMessage={!isLoading && payouts.length === 0 && isFilterActive ? 'No data available' : undefined}
      />
    </div>
  );
};
